import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import { Box, ButtonBase, CircularProgress, Collapse, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import type { AgentTurnResult } from "./agentChatState";
import type { AgentProgressStep } from "./agentProgress";
import { describeFinishedProgress, formatElapsed, toProgressSteps } from "./agentProgress";

const TICK_MILLISECONDS = 1000;

interface StepIconProps {
  status: AgentProgressStep["status"];
}

const StepIcon = ({ status }: StepIconProps) => {
  switch (status) {
    case "running":
      return <CircularProgress size="1em" color="inherit" />;
    case "failed":
      return <CloseRoundedIcon fontSize="inherit" color="error" />;
    case "stopped":
      return <StopRoundedIcon fontSize="inherit" />;
    default:
      return <CheckRoundedIcon fontSize="inherit" />;
  }
};

interface ProgressStepRowProps {
  step: AgentProgressStep;
}

const ProgressStepRow = ({ step }: ProgressStepRowProps) => (
  <Typography
    variant="caption"
    color={step.status === "running" ? "textPrimary" : "textSecondary"}
    sx={{ display: "flex", alignItems: "center", gap: 1 }}
  >
    <StepIcon status={step.status} />
    {step.text}
  </Typography>
);

interface Props {
  progress: string[];
  status: AgentTurnResult["status"];
  startedAt: number;
  endedAt?: number;
}

const ArtifactAgentProgress = ({ progress, status, startedAt, endedAt }: Props) => {
  const running = status === "running";
  const [expandedByUser, setExpandedByUser] = useState<boolean>();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!running) {
      return undefined;
    }
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MILLISECONDS);
    return () => window.clearInterval(timer);
  }, [running]);

  const steps = toProgressSteps(progress, status);
  const hasSteps = steps.length > 0;
  const expanded = hasSteps && (expandedByUser ?? running);
  const elapsed = (endedAt ?? now) - startedAt;
  const header = running ? `Working… ${formatElapsed(elapsed)}` : describeFinishedProgress(elapsed, steps.length);

  return (
    <Box>
      <ButtonBase
        disabled={!hasSteps}
        aria-expanded={expanded}
        onClick={() => setExpandedByUser(!expanded)}
        sx={{ borderRadius: 1 }}
      >
        <Typography variant="caption" color="textSecondary" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {running && <CircularProgress size="1em" color="inherit" />}
          {header}
          {hasSteps && (
            <ExpandMoreRoundedIcon
              fontSize="inherit"
              sx={{ transition: "transform 150ms", transform: expanded ? "rotate(180deg)" : undefined }}
            />
          )}
        </Typography>
      </ButtonBase>
      <Collapse in={expanded}>
        <Stack spacing={0.5} sx={{ mt: 0.5, ml: 0.75, pl: 1.5, borderLeft: 1, borderColor: "divider" }}>
          {steps.map((step, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <ProgressStepRow key={index} step={step} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default ArtifactAgentProgress;
